// ***************************************
// TGFStorage
//
// This object class deals with storing data. It uses both local
// storage and, if available, some form of remote storage. 
// Initially only local storage is supported.
//
// Copyright 2015 Tingly Games B.V.
// Last Changed: 29-6-2015
// ***************************************
import { tgfFunctions } from "./tgfFunctions";
import { tgfSettings } from "./tgfSettings";
import { defaultRandom } from "../engine/tbRandom";
import { YoutubeStorageManager } from "../code/storage/YoutubeStorageManager";
tgfSettings.storageDefaults = {
    remote: "none",
    encode: "hex",
    encodeInTestMode: false,
    version: 1,
    seperatePerTheme: true,
    seperatePerClient: true,
    autoLoadInterval: 0,
    autoSaveInterval: 5,
    userId: undefined
};
// The constructor
export var TGFStorage = function (name) {
    "use strict";
    this.name = name === undefined ? "storage" : name;
    // Initialize the data
    this.timeStamp = 0; // Last date something changed (seconds since 1970)
    this.data = {}; // The actual data to be stored
    this.dataChanged = false; // Whether the data was changed
};
// ***************************************
// Initialization
// ***************************************
// Initialize the storage (this can be asynchronous)
TGFStorage.prototype.initialize = function (settings, onSuccess, onFail) {
    "use strict";
    var _this = this;
    // Initialize the settings
    this._setProperties(tgfSettings.storageDefaults);
    this._setProperties(settings);
    this.busyLoading = false; // Whether busy loading data from remote
    this.busySaving = false; // Whether busy saving data to remote
    this.localAvailable = false; // Whether local storage is available
    this.remoteAvailable = false; // Whether remote storage is available
    this.synchronous = true; // Whether saving and loading is synchronous
    this.loadComplete = true; // Whether the last load action was successfully completed
    this.loadFailed = false; // Whether the last load action failed
    this.saveComplete = true; // Whether the last save action was successfully completed
    this.saveFailed = false; // Whether the last save action failed
    this.initComplete = false; // Whether initialization was successfully completed
    this.initFailed = false; // Whether initialization failed
    // Check whether JSON stringify is supported
    if (!JSON || !JSON.stringify) {
        this.initFailed = true;
        return;
    }
    YoutubeStorageManager.getPlayerData([], function (data) {
        _this.data = data["tgf_data"] || {};
        _this.initComplete = true;
        onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess();
    });
    // Deal with automatic loading and saving
    this.setAutoLoadInterval(this.autoLoadInterval);
    this.setAutoSaveInterval(this.autoSaveInterval);
};
// Update the system when the client id changed (e.g. due to logging in)
TGFStorage.prototype.updateId = function (newMethod) {
    throw new Error("TGFStorage.updateId not implemented");
};
// Sets the automatic load interval in seconds (<=0 is don't load automatically)
TGFStorage.prototype.setAutoLoadInterval = function (interval) {
    "use strict";
    var _this = this;
    // Clear the old timer, if any
    if (this.autoLoadTimer !== undefined) {
        window.clearInterval(this.autoLoadTimer);
    }
    // Set the new timer, if required
    this.autoLoadInterval = interval;
    if (this.autoLoadInterval > 0) {
        this.autoLoadTimer = window.setInterval(function () { TGFStorage.prototype.loadData.call(_this); }, 1000 * this.autoLoadInterval);
    }
};
// Sets the automatic save interval in seconds (<=0 is don't save automatically)
TGFStorage.prototype.setAutoSaveInterval = function (interval) {
    "use strict";
    var _this = this;
    // Clear the old timer, if any
    if (this.autoSaveTimer !== undefined) {
        window.clearInterval(this.autoSaveTimer);
    }
    // Set the new timer, if required
    this.autoSaveInterval = interval;
    if (this.autoSaveInterval > 0) {
        this.autoSaveTimer = window.setInterval(function () { TGFStorage.prototype.saveData.call(_this); }, 1000 * this.autoSaveInterval);
    }
};
// ***************************************
// Internal methods
// ***************************************
// Set a number of properties
TGFStorage.prototype._setProperties = function (properties) {
    "use strict";
    var property;
    if (typeof properties !== "object") {
        return;
    }
    for (property in properties) {
        if (properties.hasOwnProperty(property)) {
            this[property] = tgfFunctions.copyValue(properties[property]);
        }
    }
};
// ***************************************
// Dealing with keys and data
// ***************************************
// Encode in hex format with a simple shift (not meant to be secure but just to make it harder)
TGFStorage.prototype._hexEncode = function (key, str) {
    "use strict";
    var code, i, result;
    result = ("000" + key.toString(16)).slice(-4);
    for (i = 0; i < str.length; i += 1) {
        code = str.charCodeAt(i);
        code += i * key;
        code = code % 0xFFFF;
        result += ("000" + code.toString(16)).slice(-4);
    }
    return result;
};
// Decode from hex format
TGFStorage.prototype._hexDecode = function (str) {
    "use strict";
    var i, key, code, result = "", hexes = str.match(/.{1,4}/g) || [];
    key = parseInt(hexes[0], 16);
    for (i = 1; i < hexes.length; i += 1) {
        code = parseInt(hexes[i], 16);
        code -= (i - 1) * key;
        code = ((code % 0xFFFF) + 0xFFFF) % 0xFFFF;
        result += String.fromCharCode(code);
    }
    return result;
};
// Returns the storage key string based on the name
TGFStorage.prototype._createKey = function () {
    "use strict";
    var key = "TG_";
    key += this.version + "_";
    if (this.userId) {
        key += this.userId + "_";
    }
    else {
        key += "anonymous_";
    }
    if (tgfSettings.game && tgfSettings.game.id) {
        key += tgfSettings.game.id + "_";
    }
    if (this.seperatePerTheme && tgfSettings.theme && tgfSettings.theme.id) {
        key += tgfSettings.theme.id + "_";
    }
    if (this.seperatePerClient && tgfSettings.client && tgfSettings.client.id) {
        key += tgfSettings.client.id + "_";
    }
    key += this.name;
    switch (this.encode) {
        case "hex":
            key = this._hexEncode(12345, key);
            break;
    }
    return key;
};
// Returns the storage data string for the data
TGFStorage.prototype._encodeData = function () {
    "use strict";
    var result = JSON.stringify({
        "timeStamp": this.timeStamp,
        "data": JSON.stringify(this.data)
    });
    switch (this.encode) {
        case "hex":
            result = this._hexEncode(defaultRandom.iRandomRange(10000, 20000), result);
            break;
    }
    return result;
};
// ***************************************
// Fast methods for simple data
// ***************************************
// Setting the value for the key in a simple way
TGFStorage.prototype.setValueSimple = function (key, value) {
    "use strict";
    // Change the data when it is different
    if (this.data[key] !== value) {
        this.timeStamp = (new Date()).getTime();
        this.data[key] = value;
        this.dataChanged = true;
    }
};
// Getting the value for key in a simple way
TGFStorage.prototype.getValueSimple = function (key) {
    "use strict";
    return this.data[key];
};
// ***************************************
// Setting and retrieving data
// ***************************************
// Check whether a key exists
TGFStorage.prototype.keyExists = function (key) {
    "use strict";
    return this.data[key] !== undefined;
};
// Check whether a sub key exists
TGFStorage.prototype.subKeyExists = function (key, subkey) {
    "use strict";
    if (typeof this.data[key] !== "object") {
        return false;
    }
    return this.data[key][subkey] !== undefined;
};
// Setting all the data
TGFStorage.prototype.setData = function (value, save, onSuccess, onFail, noCopy) {
    "use strict";
    // Change the data when it is different
    if (!tgfFunctions.equalValues(this.data, value)) {
        this.timeStamp = (new Date()).getTime();
        if (noCopy) {
            this.data = value;
        }
        else {
            this.data = tgfFunctions.copyValue(value);
        }
        this.dataChanged = true;
    }
    // Save the data when required
    if (save && this.dataChanged) {
        this.saveData(onSuccess, onFail, false);
    }
};
// Setting the value for the key
TGFStorage.prototype.setValue = function (key, value, save, onSuccess, onFail, noCopy) {
    "use strict";
    // Change the data when it is different
    if (!tgfFunctions.equalValues(this.data[key], value)) {
        this.timeStamp = (new Date()).getTime();
        if (noCopy) {
            this.data[key] = value;
        }
        else {
            this.data[key] = tgfFunctions.copyValue(value);
        }
        this.dataChanged = true;
    }
    // Save the data when required
    if (save && this.dataChanged) {
        this.saveData(onSuccess, onFail, false);
    }
};
// Setting the value for a sub key
TGFStorage.prototype.setSubValue = function (key, subkey, value, save, onSuccess, onFail, noCopy) {
    "use strict";
    // Change the data when it is different
    if (typeof this.data[key] !== "object") {
        return;
    }
    if (!tgfFunctions.equalValues(this.data[key][subkey], value)) {
        this.timeStamp = (new Date()).getTime();
        if (noCopy) {
            this.data[key][subkey] = value;
        }
        else {
            this.data[key][subkey] = tgfFunctions.copyValue(value);
        }
        this.dataChanged = true;
    }
    // Save the data when required
    if (save && this.dataChanged) {
        this.saveData(onSuccess, onFail, false);
    }
};
// Set multiple data values
TGFStorage.prototype.setValues = function (values, save, onSuccess, onFail, noCopy) {
    "use strict";
    var key;
    if (typeof values !== "object") {
        return;
    }
    // Change the data when it is different
    for (key in values) {
        if (values.hasOwnProperty(key)) {
            if (!tgfFunctions.equalValues(this.data[key], values[key])) {
                this.timeStamp = (new Date()).getTime();
                if (noCopy) {
                    this.data[key] = values[key];
                }
                else {
                    this.data[key] = tgfFunctions.copyValue(values[key]);
                }
                this.dataChanged = true;
            }
        }
    }
    // Save the data when required
    if (save && this.dataChanged) {
        this.saveData(onSuccess, onFail, false);
    }
};
// Getting a copy of the data
TGFStorage.prototype.getData = function (noCopy) {
    "use strict";
    if (noCopy) {
        return this.data;
    }
    else {
        return tgfFunctions.copyValue(this.data);
    }
};
// Getting the value for key
TGFStorage.prototype.getValue = function (key, noCopy) {
    "use strict";
    if (noCopy) {
        return this.data[key];
    }
    else {
        return tgfFunctions.copyValue(this.data[key]);
    }
};
// Getting the value for a sub key
TGFStorage.prototype.getSubValue = function (key, subkey, noCopy) {
    "use strict";
    if (typeof this.data[key] !== "object") {
        return undefined;
    }
    if (noCopy) {
        return this.data[key][subkey];
    }
    else {
        return tgfFunctions.copyValue(this.data[key][subkey]);
    }
};
// Loading all the data
TGFStorage.prototype.loadData = function (onSuccess, onFail, force) {
    "use strict";
    var data, key;
    // Check whether we are loading or saving
    if (this.busyLoading || this.busySaving) {
        if (onFail) {
            onFail();
        }
        return;
    }
    // If force, reset the current timestamp
    if (force) {
        this.timeStamp = 0;
    }
    // Load the local data
    data = this.data;
    this.loadComplete = true;
    if (onSuccess) {
        onSuccess();
    }
};
// Saving all the data
TGFStorage.prototype.saveData = function (onSuccess, onFail, force) {
    "use strict";
    var _this = this;
    var data, key;
    // Check whether there is anything to save
    if (!this.dataChanged && !force) {
        return;
    }
    // Check whether we are loading or saving
    if (this.busyLoading || this.busySaving) {
        if (onFail) {
            onFail();
        }
        return;
    }
    this.saveComplete = false;
    this.saveFailed = false;
    this.busySaving = true;
    YoutubeStorageManager.savePlayerData(["tgf_data"], [this.data], function () { _this.busySaving = false; _this.saveComplete = true; if (onSuccess) {
        onSuccess();
    } }, function () { _this.busySaving = false; _this.saveFailed = true; if (onFail) {
        onFail();
    } });
    // Ready
    this.dataChanged = false;
};
